import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Row, Col, Form, Button } from "react-bootstrap";
import { FormValidator } from "../../helpers/form-validator";
import { Validator } from "../widgets";
import {
  createFarm,
  editFarm,
  getTimeZoneList,
  getFarmList,
} from "../../actions/farm";
import { getOrganizationList } from "../../actions/organization";
import { getUserId } from "../../helpers/session";
import { LOAD_MODE, LOAD_TYPE } from "../../constants";

const getRules = () => {
  const isSelect = (value) => {
    return value === null || value === "";
  };

  return [
    {
      field: "farmName",
      method: "isEmpty",
      validWhen: false,
      message: "Farm Name is required.",
    },
    {
      field: "login",
      method: "isEmpty",
      validWhen: false,
      message: "Login is required.",
    },
    {
      field: "password",
      method: "isEmpty",
      validWhen: false,
      message: "Password is required.",
    },
    {
      field: "timezone",
      method: isSelect,
      validWhen: false,
      message: "Timezone is required",
    },
    {
      field: "organization",
      method: isSelect,
      validWhen: false,
      message: "Organization is required",
    },
  ];
};

const FarmForm = (props) => {
  const {
    onHide,
    isEditFarmName,
    currentFarmName,
    currentFarmTimezone,
    currentFarmUuid,
    currentFarmLoadType,
    currentFarmLoadMode,
  } = props;

  const validator = new FormValidator(getRules());
  const dispatch = useDispatch();

  const timeZoneList = useSelector((state) => state.farm.timeZoneList);
  const organizationList = useSelector(
    (state) => state.organization.organizationList
  );

  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [state, setState] = useState({
    validation: validator.valid(),
    farmName: `${isEditFarmName ? currentFarmName : ""}`,
    login: "",
    password: "",
    timezone: `${isEditFarmName ? Number(currentFarmTimezone) : null}`,
    organization: null,
    loadType: isEditFarmName ? currentFarmLoadType : LOAD_TYPE.AUTOMATIC,
    chatOption: true,
    ...(isEditFarmName && { loadMode: currentFarmLoadMode }),
  });

  useEffect(() => {
    if (timeZoneList.length === 0) dispatch(getTimeZoneList());
    if (organizationList.length === 0) dispatch(getOrganizationList());
  }, []);

  useEffect(() => {
    // set default organization list when creating farm
    const { organization_uuid } =
      organizationList.find((o) => o.is_default) || {};
    if (organization_uuid)
      setState({ ...state, organization: organization_uuid });
  }, [organizationList]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setState({ ...state, [name]: value });
  };

  const handleChoose = (event) => {
    const { name, value } = event.target;
    setState({ ...state, [name]: parseInt(value) });
  };

  const handleChatOption = (event) => {
    const { name, checked } = event.target;
    setState({ ...state, [name]: checked });
  };

  const handleSelect = (item, event) => {
    const { name } = event;
    setState({ ...state, [name]: item ? item.value : null });
  };

  const saveFarm = () => {
    const s = state;
    const validation = validator.validate(s);

    setState({ ...state, validation });
    setSubmitted(true);
    if (isEditFarmName && s && s.timezone && s.farmName.trim().length > 0) {
      const params = {
        farm_name: s.farmName,
        farm_uuid: currentFarmUuid,
        load_type: s.loadType,
        load_mode: s.loadMode ? s.loadMode : LOAD_MODE.FIELD_CENTRIC,
        timezone_id: Number(s.timezone),
      };

      const cb = () => {
        setIsSaving(false);
        dispatch(getFarmList(false));
        onHide();
      };
      setIsSaving(true);
      dispatch(editFarm(params, cb));
    }
    if (validation.isValid && !isEditFarmName) {
      const params = {
        farm_name: s.farmName,
        organization_uuid: s.organization,
        login: s.login,
        password: s.password,
        timezone_id: s.timezone,
        load_type: s.loadType,
        load_mode: s.loadMode ? s.loadMode : LOAD_MODE.FIELD_CENTRIC,
        created_by: getUserId(),
        with_qapp_credential: true,
        is_chat_enable: s.chatOption,
      };

      const cb = (isSuccess) => {
        setIsSaving(false);
        if (isSuccess) {
          dispatch(getFarmList(false));
          onHide();
        }
      };

      setIsSaving(true);
      dispatch(createFarm(params, cb));
    }
  };

  const getOrganizationListOptions = (organizationList) => {
    return organizationList.reduce((acc, item) => {
      const option = {
        value: item.organization_uuid,
        label: item.organization_name,
      };
      Boolean(item.is_default) ? acc.unshift(option) : acc.push(option);
      return acc;
    }, []);
  };

  const s = state;
  const v = submitted ? validator.validate(state) : state.validation;

  return (
    <Modal
      show={true}
      onHide={onHide}
      backdrop="static"
      className="farm-modal-form"
    >
      <Modal.Header closeButton>
        <Modal.Title>{`${
          isEditFarmName ? "Edit Farm" : "Add Farm"
        }`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form autoComplete="off">
          <Form.Group>
            <Validator validation={v.farmName}>
              <Form.Label htmlFor="txtFarmName">
                Farm Name
                <Validator.Mand />
              </Form.Label>
              <Form.Control
                type="text"
                id="txtFarmName"
                name="farmName"
                placeholder="Enter the farm name"
                maxLength="50"
                value={s.farmName}
                onChange={handleChange}
                autoComplete="off"
              />
              <Validator.Text />
            </Validator>
          </Form.Group>

          {!isEditFarmName ? (
            <Form.Group>
              <Validator validation={v.organization}>
                <Form.Label>
                  Organization
                  <Validator.Mand />
                </Form.Label>
                <Form.Select
                  name="organization"
                  className="select-control-sm"
                  placeholder="Select organization"
                  value={s.organization}
                  options={getOrganizationListOptions(organizationList)}
                  onChange={handleSelect}
                ></Form.Select>
                <Validator.Text />
              </Validator>
            </Form.Group>
          ) : null}

          <Form.Group>
            <Validator validation={v.timezone}>
              <Form.Label>
                Timezone
                <Validator.Mand />
              </Form.Label>
              <Form.Select
                name="timezone"
                className="select-control-sm"
                placeholder="Select timezone"
                value={Number(s.timezone)}
                options={timeZoneList.map((item) => ({
                  value: item.timezone_id,
                  label: item.timezone,
                }))}
                onChange={handleSelect}
                sortType="asc"
              ></Form.Select>
              <Validator.Text />
            </Validator>
          </Form.Group>

          {!isEditFarmName ? (
            <>
              <Form.Group>
                <Validator validation={v.login}>
                  <Form.Label htmlFor="txtLogin">
                    Q-app Login
                    <Validator.Mand />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id="txtLogin"
                    name="login"
                    placeholder="Enter the Q-app login"
                    maxLength="50"
                    value={s.login}
                    onChange={handleChange}
                  />
                  <Validator.Text />
                </Validator>
              </Form.Group>
              <Form.Group>
                <Validator validation={v.password}>
                  <Form.Label htmlFor="txtPassword">
                    Q-app Password
                    <Validator.Mand />
                  </Form.Label>
                  <Form.Control
                    type="password"
                    id="txtPassword"
                    name="password"
                    placeholder="Enter the Q-app password"
                    maxLength="50"
                    value={s.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                  <Validator.Text />
                </Validator>
              </Form.Group>
            </>
          ) : null}

          <Form.Group>
            <Form.Label>Load Type</Form.Label>
            <Row className="ml-3">
              <Form.Check
                type="radio"
                name="loadType"
                value={LOAD_TYPE.AUTOMATIC}
                inline
                label="Automatic"
                id="automatic"
                checked={state.loadType === LOAD_TYPE.AUTOMATIC}
                onChange={handleChoose}
              />
              <Form.Check
                type="radio"
                name="loadType"
                value={LOAD_TYPE.MANUAL}
                inline
                label="Manual"
                id="manual"
                checked={state.loadType === LOAD_TYPE.MANUAL}
                onChange={handleChoose}
              />
            </Row>
          </Form.Group>

          {isEditFarmName && (
            <Form.Group>
              <Form.Label>Load Mode</Form.Label>
              <Row className="ml-3">
                <Form.Check
                  type="radio"
                  name="loadMode"
                  value={LOAD_MODE.FIELD_CENTRIC}
                  inline
                  label="Field Exit"
                  id="field"
                  checked={state.loadMode === LOAD_MODE.FIELD_CENTRIC}
                  onChange={handleChoose}
                />
                <Form.Check
                  type="radio"
                  name="loadMode"
                  value={LOAD_MODE.OFFLOAD_CENTRIC}
                  inline
                  label="Offload Entry"
                  id="offload"
                  checked={state.loadMode === LOAD_MODE.OFFLOAD_CENTRIC}
                  onChange={handleChoose}
                />
              </Row>
            </Form.Group>
          )}

          {!isEditFarmName ? (
            <Form.Group>
              <Row className="ml-1">
                <Form.Check
                  type="checkbox"
                  name="chatOption"
                  label="Chat Applicable"
                  id="chatOption"
                  checked={state.chatOption}
                  onChange={handleChatOption}
                />
              </Row>
            </Form.Group>
          ) : null}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Col className="p-0">
          <Button
            variant="primary"
            size="sm"
            className="float-right mx-1"
            onClick={saveFarm}
          >
            <i className="fa fa-floppy-o"></i>
            {isSaving ? " Saving..." : " Save"}
          </Button>
          <Button
            variant="danger"
            size="sm"
            className="float-right mx-2"
            onClick={onHide}
          >
            <i className="fa fa-times-circle"></i> Cancel
          </Button>
        </Col>
      </Modal.Footer>
    </Modal>
  );
};

export default FarmForm;
